// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — useProcessesByGroup hook
// Fetches Level 2 data: processes belonging to a specific group
// Filters by grupoNumero + tipologia (grupoNumero is NOT unique)
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import type { ProcessGroup, ProcessSummary } from '../../flow/types/flowchart.types';
import { useI18n } from '../../../../../i18n';
import { request } from '../../../../shared/services/api';

export function useProcessesByGroup(groupId: number | undefined, tipologia?: string) {
  const { lang } = useI18n();
  const [group, setGroup] = useState<ProcessGroup | null>(null);
  const [procesos, setProcesos] = useState<ProcessSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (groupId === undefined || isNaN(groupId)) return;
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        params.set('grupoNumero', String(groupId));
        if (tipologia) params.set('tipologia', tipologia);

        type ProcesoDTO = {
          id: string;
          codigo: string;
          nombre: string;
          tipologia: 'PE' | 'PO' | 'PS';
          grupoNumero: number;
          grupoNombre: string;
          fase?: string;
          responsable?: { titulo: string };
          _count?: { documentos: number };
        };
        const json = await request<{ data?: ProcesoDTO[]; datos?: ProcesoDTO[] }>(
          `/procesos?${params}`,
        );
        const filtered: ProcesoDTO[] = json.data ?? json.datos ?? [];

        if (!Array.isArray(filtered) || filtered.length === 0) {
          if (!cancelled) {
            setGroup(null);
            setProcesos([]);
            setError('Grupo no encontrado');
          }
          return;
        }

        const first = filtered[0];
        const groupData: ProcessGroup = {
          grupoNumero: first.grupoNumero,
          grupoNombre: first.grupoNombre,
          tipologia: first.tipologia,
          procesos: filtered.map((p) => ({
            id: p.id,
            codigo: p.codigo,
            nombre: p.nombre,
            fase: p.fase,
            responsable: p.responsable,
            documentCount: p._count?.documentos ?? 0,
          })),
        };

        if (!cancelled) {
          setGroup(groupData);
          setProcesos(groupData.procesos);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [groupId, tipologia, lang]);

  return { group, procesos, loading, error };
}
